import { Box, Flex, Input, useColorModeValue } from '@chakra-ui/react';
import React from 'react';

import type { MethodType } from './types';

import { useI18n } from 'lib/i18n/context';
import Skeleton from 'ui/shared/chakra/Skeleton';
import ClearButton from 'ui/shared/ClearButton';
import IconSvg from 'ui/shared/IconSvg';

import { ADDRESS_CONTRACT_KEYS, CONTRACT_METHOD_FILTER_OPTION_KEYS } from '../../i18n';
import type { MethodsFilters } from './useMethodsFilters';

interface Props {
  methodType: MethodType;
  searchTerm: string;
  onChange: (filter: MethodsFilters) => void;
  isLoading?: boolean;
}

const ContractMethodsFilters = ({ methodType, searchTerm, onChange, isLoading }: Props) => {
  const { t } = useI18n();
  const inputRef = React.useRef<HTMLInputElement>(null);

  const groupBg = useColorModeValue('#F6F8FA', '#171717');
  const groupBorder = useColorModeValue('#E9ECEF', '#222222');
  const activeBg = useColorModeValue('#0FA88A', '#14DCAC');
  const activeText = useColorModeValue('#FFFFFF', '#000000');
  const inactiveText = '#616161';
  const searchBg = useColorModeValue('#F6F8FA', '#171717');
  const searchBorder = useColorModeValue('#E9ECEF', '#222222');
  const searchText = useColorModeValue('#000000', '#FFFFFF');
  const searchPlaceholder = useColorModeValue('#999999', '#616161');
  const searchIconColor = useColorModeValue('#999999', '#616161');

  const typeFilterOptions = React.useMemo(() => CONTRACT_METHOD_FILTER_OPTION_KEYS.map((option) => ({
    value: option.value,
    title: t(option.titleKey),
  })), [ t ]);

  const handleTypeChange = React.useCallback((value: MethodType) => {
    onChange({ type: 'method_type', value });
  }, [ onChange ]);

  const handleTypeButtonClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    const { value } = event.currentTarget.dataset;

    if (value) {
      handleTypeChange(value as MethodType);
    }
  }, [ handleTypeChange ]);

  const handleSearchTermChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ type: 'method_name', value: event.target.value });
  }, [ onChange ]);

  const handleSearchTermClear = React.useCallback(() => {
    onChange({ type: 'method_name', value: '' });
    inputRef.current?.focus();
  }, [ onChange ]);

  return (
    <Skeleton isLoaded={ !isLoading } w="100%">
      <Flex
        alignItems="center"
        justifyContent="space-between"
        columnGap={ 3 }
        rowGap={ 3 }
        flexWrap={{ base: 'wrap', lg: 'nowrap' }}
        w="100%"
      >
        <Flex
          bg={ groupBg }
          border="1px solid"
          borderColor={ groupBorder }
          borderRadius="4px"
          h="32px"
          overflow="hidden"
          flexShrink={ 0 }
        >
          { typeFilterOptions.map((option) => {
            const isActive = methodType === option.value;

            return (
              <Box
                key={ option.value }
                as="button"
                type="button"
                h="32px"
                w="80px"
                px={ 4 }
                fontSize="14px"
                fontWeight={ 500 }
                lineHeight="22px"
                textAlign="center"
                bg={ isActive ? activeBg : 'transparent' }
                color={ isActive ? activeText : inactiveText }
                cursor="pointer"
                data-value={ option.value }
                onClick={ handleTypeButtonClick }
                _hover={{ opacity: isActive ? 1 : 0.85 }}
                _focus={{ outline: 'none' }}
              >
                { option.title }
              </Box>
            );
          }) }
        </Flex>

        <Flex
          alignItems="center"
          gap={ 2 }
          h="32px"
          w={{ base: '100%', lg: '320px' }}
          flexShrink={ 0 }
          bg={ searchBg }
          border="1px solid"
          borderColor={ searchBorder }
          borderRadius="8px"
          pl={ 4 }
          pr={ 2 }
        >
          <IconSvg name="search" boxSize={ 5 } color={ searchIconColor } flexShrink={ 0 }/>
          <Input
            ref={ inputRef }
            value={ searchTerm }
            onChange={ handleSearchTermChange }
            placeholder={ t(ADDRESS_CONTRACT_KEYS.filters.searchByMethodName) }
            variant="unstyled"
            h="100%"
            flex={ 1 }
            minW={ 0 }
            fontSize="14px"
            fontWeight={ 500 }
            lineHeight="24px"
            color={ searchText }
            _placeholder={{ color: searchPlaceholder }}
            _focusVisible={{ boxShadow: 'none' }}
          />
          { searchTerm && <ClearButton onClick={ handleSearchTermClear }/> }
        </Flex>
      </Flex>
    </Skeleton>
  );
};

export default React.memo(ContractMethodsFilters);
